// Neon Snake.io Arena Server Engine (Responsive 800x520, Strict Bounds & Instant Rebirth)
export class SnakeEngine {
  constructor(io, room) {
    this.io = io;
    this.room = room;
    this.arenaWidth = 800;
    this.arenaHeight = 520;
    this.snakes = {};
    this.foods = [];
    this.loopInterval = null;
  }

  start() {
    this.snakes = {};
    this.foods = [];

    const colors = ['#22c55e', '#38bdf8', '#eab308', '#ec4899', '#a855f7', '#f97316', '#ef4444', '#06b6d4'];

    this.room.players.forEach((p, idx) => {
      this.initSnake(p.id, p.name, colors[idx % colors.length], !!p.isBot);
    });

    // If solo player testing, add AI bot snakes!
    if (this.room.players.length === 1) {
      this.initSnake('bot_viper', 'Viper Bot 🐍', '#ec4899', true);
      this.initSnake('bot_cobra', 'Cobra Bot 🐍', '#eab308', true);
    }

    // Spawn 60 food pellets
    for (let i = 0; i < 60; i++) {
      this.spawnFood();
    }

    if (this.loopInterval) clearInterval(this.loopInterval);
    this.loopInterval = setInterval(() => this.updateLoop(), 1000 / 30);
  }

  initSnake(id, name, color, isBot = false) {
    const startX = 150 + Math.random() * (this.arenaWidth - 300);
    const startY = 100 + Math.random() * (this.arenaHeight - 200);
    const angle = Math.random() * Math.PI * 2;

    this.snakes[id] = {
      id,
      name,
      color,
      body: [{ x: startX, y: startY }],
      length: 15,
      speed: 4.2,
      angle,
      targetAngle: angle,
      score: 0,
      isAlive: true,
      invulnFrames: 60, // 2s invulnerability on spawn
      isBot
    };
  }

  respawnSnake(playerId) {
    const existing = this.snakes[playerId];
    if (existing) {
      this.initSnake(playerId, existing.name, existing.color, existing.isBot);
    }
  }

  spawnFood() {
    this.foods.push({
      x: 20 + Math.random() * (this.arenaWidth - 40),
      y: 20 + Math.random() * (this.arenaHeight - 40),
      color: ['#facc15', '#38bdf8', '#ec4899', '#4ade80', '#c084fc'][Math.floor(Math.random() * 5)]
    });
  }

  handleInput(playerId, angle) {
    const s = this.snakes[playerId];
    if (s && s.isAlive) {
      s.targetAngle = angle;
    }
  }

  updateLoop() {
    for (const id in this.snakes) {
      const s = this.snakes[id];
      if (!s.isAlive) continue;

      if (s.invulnFrames > 0) s.invulnFrames--;

      // AI Bot Navigation
      if (s.isBot && Math.random() < 0.06) {
        // Steer toward closest food
        let closest = null;
        let minDist = 999;
        this.foods.forEach((f) => {
          const d = Math.hypot(f.x - s.body[0].x, f.y - s.body[0].y);
          if (d < minDist) { minDist = d; closest = f; }
        });
        if (closest) {
          s.targetAngle = Math.atan2(closest.y - s.body[0].y, closest.x - s.body[0].x);
        }
      }

      // Smooth steering
      let diff = s.targetAngle - s.angle;
      while (diff < -Math.PI) diff += Math.PI * 2;
      while (diff > Math.PI) diff -= Math.PI * 2;
      s.angle += diff * 0.25;

      const head = s.body[0];
      const newHead = {
        x: head.x + Math.cos(s.angle) * s.speed,
        y: head.y + Math.sin(s.angle) * s.speed
      };

      // STRICT SCREEN BOUNDARIES (Kill on wall hit)
      if (newHead.x < 12 || newHead.x > this.arenaWidth - 12 || newHead.y < 12 || newHead.y > this.arenaHeight - 12) {
        this.killSnake(s);
        continue;
      }

      s.body.unshift(newHead);
      while (s.body.length > s.length) {
        s.body.pop();
      }

      // Food Eating
      for (let i = this.foods.length - 1; i >= 0; i--) {
        const f = this.foods[i];
        if (Math.hypot(newHead.x - f.x, newHead.y - f.y) < 16) {
          this.foods.splice(i, 1);
          s.length += 2;
          s.score += 10;
          this.spawnFood();
        }
      }

      // Snake Body Collisions
      if (s.invulnFrames <= 0) {
        for (const otherId in this.snakes) {
          if (otherId === id) continue;
          const other = this.snakes[otherId];
          if (!other.isAlive) continue;

          for (let b = 0; b < other.body.length; b += 2) {
            if (Math.hypot(newHead.x - other.body[b].x, newHead.y - other.body[b].y) < 14) {
              this.killSnake(s);
              break;
            }
          }
        }
      }
    }

    this.io.to(this.room.code).emit('snake_state', {
      snakes: this.snakes,
      foods: this.foods,
      arenaWidth: this.arenaWidth,
      arenaHeight: this.arenaHeight
    });
  }

  killSnake(s) {
    s.isAlive = false;
    // Explode into food orbs
    s.body.forEach((pt, idx) => {
      if (idx % 2 === 0) {
        this.foods.push({ x: pt.x, y: pt.y, color: '#fbbf24' });
      }
    });

    this.io.to(this.room.code).emit('snake_died', {
      playerId: s.id,
      name: s.name
    });
  }

  destroy() {
    if (this.loopInterval) clearInterval(this.loopInterval);
  }
}
